import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { UserPlus, Ban, Check, ShieldOff, Link2, Unlink, UserX, UserCheck, LogOut, ShieldCheck, KeySquare, TriangleAlert } from "lucide-react";
import { toast } from "sonner";
import { authApi } from "../../services/authApi";
import { invitationApi } from "../../services/invitationApi";
import { employeeApi } from "../../services/employeeApi";
import { mfaApi } from "../../services/mfaApi";
import { formatApiErrorDetail } from "../../lib/api";
import { ROLES, rolesForDomain } from "../../lib/constants";
import { profileLabelForRole, profileDescriptionForRole } from "../../lib/accessProfiles";
import { useAuth } from "../../hooks/useAuth";
import { usePermissions } from "../../hooks/usePermissions";
import { useLocalization } from "../../hooks/useLocalization";
import { useRouteRefresh } from "../../hooks/useRouteRefresh";
import { StatusBadge } from "../../components/StatusBadge";
import { PageHelpBanner } from "../../components/PageHelpBanner";
import { ConfirmDialog } from "../../components/ConfirmDialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";

const CAN_RESET_MFA_ROLES = [ROLES.SYSTEM_OWNER, ROLES.SECURITY_ADMIN];

export default function ManagementUserDirectoryPage() {
  const { t } = useLocalization();
  const { user } = useAuth();
  const { canManageUsers, canCreateManagementUser, canAssignManagementRole, canGrantExtraPermissions } = usePermissions();
  const canResetMfa = CAN_RESET_MFA_ROLES.includes(user?.role);
  const [users, setUsers] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [resetTargetId, setResetTargetId] = useState(null);
  const [resetReason, setResetReason] = useState("");
  const [resetError, setResetError] = useState("");
  const [linkTargetId, setLinkTargetId] = useState(null);
  const [linkEmployeeId, setLinkEmployeeId] = useState("");
  const [accessDialogUser, setAccessDialogUser] = useState(null);
  const [accessProfileChoice, setAccessProfileChoice] = useState("");
  const [revokingInvitationId, setRevokingInvitationId] = useState(null);
  const [suspendingUserId, setSuspendingUserId] = useState(null);
  const [revokingSessionsUserId, setRevokingSessionsUserId] = useState(null);
  const [togglingAuthorityUserId, setTogglingAuthorityUserId] = useState(null);
  const [unlinkingUserId, setUnlinkingUserId] = useState(null);

  const loadUsers = () => authApi.listUsers().then((r) => setUsers(r.data)).catch(() => {});
  const loadInvitations = () => invitationApi.list().then((r) => setInvitations(r.data)).catch(() => {});
  const loadEmployees = () => employeeApi.list().then((r) => setEmployees(r.data)).catch(() => {});

  const loadAll = () => { loadUsers(); loadInvitations(); loadEmployees(); };

  useEffect(() => {
    if (!canManageUsers) return;
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canManageUsers]);
  useRouteRefresh(() => canManageUsers && loadAll());

  if (!canManageUsers) {
    return (
      <div className="max-w-6xl" data-testid="management-user-directory-access-denied">
        <p className="text-sm text-gray-500">{t("common.accessDenied")}</p>
      </div>
    );
  }

  const handleRevoke = async (id) => {
    await invitationApi.revoke(id);
    loadInvitations();
  };

  const handleResetMfa = async (e) => {
    e.preventDefault();
    setResetError("");
    if (resetReason.trim().length < 10) {
      setResetError(t("mfa.reasonTooShort"));
      return;
    }
    try {
      await mfaApi.reset(resetTargetId, resetReason.trim());
      toast.success(t("mfa.resetMfaSuccess"));
      setResetTargetId(null);
      setResetReason("");
      loadUsers();
    } catch (err) {
      setResetError(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const handleAccessChange = async () => {
    if (!accessProfileChoice || accessProfileChoice === accessDialogUser.role) { setAccessDialogUser(null); return; }
    try {
      await authApi.updateUserRole(accessDialogUser.id, accessProfileChoice);
      toast.success(t("invitations.roleUpdated"));
      setAccessDialogUser(null);
      loadUsers();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const handleSuspendToggle = async (targetUser) => {
    if (targetUser.is_suspended) {
      try { await authApi.reactivateUser(targetUser.id); toast.success(t("invitations.reactivated")); loadUsers(); }
      catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    } else {
      setSuspendingUserId(targetUser.id);
    }
  };
  const confirmSuspend = async () => {
    try { await authApi.suspendUser(suspendingUserId); toast.success(t("invitations.suspended")); loadUsers(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const confirmRevokeSessions = async () => {
    try { await authApi.revokeSessions(revokingSessionsUserId); toast.success(t("invitations.sessionsRevoked")); loadUsers(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const confirmToggleManagementAuthority = async () => {
    const targetUser = users.find((u) => u.id === togglingAuthorityUserId);
    const currentlyGranted = (targetUser?.extra_permissions || []).includes("management.user.create");
    try {
      const next = currentlyGranted ? [] : ["management.user.create", "management.role.assign"];
      await authApi.grantExtraPermissions(togglingAuthorityUserId, next);
      toast.success(t("invitations.extraPermissionsUpdated"));
      loadUsers();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const confirmUnlink = async () => {
    try {
      await authApi.unlinkEmployee(unlinkingUserId);
      toast.success(t("invitations.unlinked"));
      loadUsers();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const handleLink = async (targetUser) => {
    if (!linkEmployeeId) return;
    try {
      await authApi.linkEmployee(targetUser.id, linkEmployeeId);
      toast.success(t("invitations.linked"));
      setLinkTargetId(null);
      setLinkEmployeeId("");
      loadUsers();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const unlinkedEmployees = employees.filter((emp) => !emp.user_id);
  const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString() : t("invitations.never"));

  return (
    <div className="max-w-6xl" data-testid="management-user-directory-page">
      <PageHelpBanner pageKey="user-accounts" title={t("pageHelp.defaultTitle")}>
        <p dangerouslySetInnerHTML={{ __html: t("invitations.helpBody1") }} />
        <p>{t("invitations.helpBody2Before")} <Link to="/management/security/access-profiles" className="underline font-semibold">{t("nav.accessProfiles")}</Link>{t("invitations.helpBody2After")}</p>
      </PageHelpBanner>

      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold tracking-tight text-gray-900" data-testid="user-accounts-page-title">{t("invitations.userAccountsPageTitle")}</h2>
        {canCreateManagementUser && (
          <Link
            to="/management/security/users/new"
            data-testid="new-management-user-button"
            className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 active:scale-95 transition-all"
          >
            <UserPlus size={16} /> {t("invitations.createManagementAccount")}
          </Link>
        )}
      </div>

      <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">{t("invitations.pendingInvitations")}</h3>
      <div className="bg-white rounded-xl border border-gray-200 overflow-x-auto mb-8">
        <table className="w-full text-sm min-w-[640px]">
          <thead className="bg-gray-50 text-gray-500 text-left">
            <tr>
              <th className="px-4 py-3 font-medium">{t("invitations.name")}</th>
              <th className="px-4 py-3 font-medium">{t("auth.email")}</th>
              <th className="px-4 py-3 font-medium">{t("invitations.domain")}</th>
              <th className="px-4 py-3 font-medium">{t("invitations.role")}</th>
              <th className="px-4 py-3 font-medium">{t("invitations.status")}</th>
              <th className="px-4 py-3 font-medium">{t("common.actions")}</th>
            </tr>
          </thead>
          <tbody>
            {invitations.length === 0 && (
              <tr><td colSpan={6} className="px-4 py-6 text-center text-gray-500" data-testid="no-invitations">{t("invitations.noInvitations")}</td></tr>
            )}
            {invitations.map((inv) => (
              <tr key={inv.id} className="border-t border-gray-100" data-testid={`invitation-row-${inv.id}`}>
                <td className="px-4 py-3">{inv.name}</td>
                <td className="px-4 py-3">{inv.email}</td>
                <td className="px-4 py-3 text-xs font-semibold text-gray-500">{inv.account_domain}</td>
                <td className="px-4 py-3">{inv.role}</td>
                <td className="px-4 py-3"><StatusBadge status={inv.status} /></td>
                <td className="px-4 py-3">
                  {(inv.status === "PENDING" || inv.status === "OPENED") ? (
                    <button data-testid={`revoke-invitation-${inv.id}`} onClick={() => setRevokingInvitationId(inv.id)} className="flex items-center gap-1 text-xs font-semibold text-red-500 hover:text-red-700">
                      <Ban size={13} /> {t("invitations.revoke")}
                    </button>
                  ) : inv.status === "COMPLETED" ? (
                    <span className="flex items-center gap-1 text-xs text-emerald-600"><Check size={13} /> {t("invitations.activeStatus")}</span>
                  ) : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">{t("invitations.accountsTitle")}</h3>
      <div className="bg-white rounded-xl border border-gray-200 overflow-x-auto">
        <table className="w-full text-sm min-w-[640px]">
          <thead className="bg-gray-50 text-gray-500 text-left">
            <tr>
              <th className="px-4 py-3 font-medium">{t("invitations.name")}</th>
              <th className="px-4 py-3 font-medium">{t("auth.email")}</th>
              <th className="px-4 py-3 font-medium">{t("invitations.domain")}</th>
              <th className="px-4 py-3 font-medium">{t("invitations.accessProfileColumn")}</th>
              <th className="px-4 py-3 font-medium">{t("invitations.linkedEmployee")}</th>
              <th className="px-4 py-3 font-medium">{t("mfa.securityTitle")}</th>
              <th className="px-4 py-3 font-medium">{t("invitations.lastLogin")}</th>
              <th className="px-4 py-3 font-medium">{t("invitations.createdAt")}</th>
              <th className="px-4 py-3 font-medium">{t("common.actions")}</th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => {
              const isSelf = u.id === user?.id;
              const linkedEmployee = employees.find((e) => e.id === u.employee_id);
              const roleAssignAllowed = canAssignManagementRole && !isSelf;
              const hasManagementAuthority = (u.extra_permissions || []).includes("management.user.create");
              return (
                <tr key={u.id} className="border-t border-gray-100" data-testid={`user-row-${u.id}`}>
                  <td className="px-4 py-3 whitespace-nowrap">
                    {u.name}
                    {u.is_suspended && <span className="ml-2"><StatusBadge status="SUSPENDED" /></span>}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">{u.email}</td>
                  <td className="px-4 py-3 text-xs font-semibold text-gray-500">{u.account_domain}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-slate-700" data-testid={`access-profile-label-${u.id}`}>{profileLabelForRole(u.role, t)}</span>
                      {roleAssignAllowed && (
                        <button
                          data-testid={`change-access-button-${u.id}`}
                          onClick={() => { setAccessDialogUser(u); setAccessProfileChoice(u.role); }}
                          className="flex items-center gap-1 text-[11px] font-semibold text-blue-600 hover:underline"
                        >
                          <KeySquare size={11} /> {t("invitations.changeAccess")}
                        </button>
                      )}
                    </div>
                    {canGrantExtraPermissions && u.role === ROLES.SYSTEM_ADMIN && (
                      <label className="flex items-center gap-1.5 mt-1.5 text-[11px] text-amber-600" data-testid={`extra-permission-toggle-${u.id}`} title={t("invitations.advancedPermissionsTooltip")}>
                        <input
                          type="checkbox"
                          checked={hasManagementAuthority}
                          onChange={() => setTogglingAuthorityUserId(u.id)}
                          className="accent-amber-600"
                        />
                        <TriangleAlert size={11} /> {t("invitations.advancedPermissionsPrefix")} {t("invitations.grantManagementAuthority")}
                      </label>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    {u.account_domain !== "MANAGEMENT" ? (
                      <span className="text-xs text-gray-500">{linkedEmployee ? `${linkedEmployee.employee_id}` : "-"}</span>
                    ) : linkedEmployee ? (
                      <button
                        data-testid={`unlink-employee-${u.id}`}
                        disabled={isSelf}
                        title={isSelf ? t("invitations.cannotModifySelf") : undefined}
                        onClick={() => setUnlinkingUserId(u.id)}
                        className="flex items-center gap-1 text-xs font-semibold text-gray-500 hover:text-red-600 disabled:opacity-40 disabled:cursor-not-allowed"
                      >
                        <Unlink size={12} /> {linkedEmployee.employee_id}
                      </button>
                    ) : linkTargetId === u.id ? (
                      <div className="flex items-center gap-1.5">
                        <select
                          data-testid={`link-employee-select-${u.id}`}
                          value={linkEmployeeId}
                          onChange={(e) => setLinkEmployeeId(e.target.value)}
                          className="px-2 py-1 rounded-lg border border-gray-300 text-xs"
                        >
                          <option value="">{t("invitations.selectEmployeeToLink")}</option>
                          {unlinkedEmployees.map((emp) => <option key={emp.id} value={emp.id}>{emp.employee_id} - {emp.legal_name}</option>)}
                        </select>
                        <button data-testid={`confirm-link-employee-${u.id}`} onClick={() => handleLink(u)} className="text-xs font-semibold text-blue-600 hover:underline">
                          {t("common.save")}
                        </button>
                      </div>
                    ) : (
                      <button
                        data-testid={`link-employee-button-${u.id}`}
                        disabled={isSelf}
                        title={isSelf ? t("invitations.cannotModifySelf") : undefined}
                        onClick={() => { setLinkTargetId(u.id); setLinkEmployeeId(""); }}
                        className="flex items-center gap-1 text-xs font-semibold text-blue-600 hover:underline disabled:opacity-40 disabled:cursor-not-allowed"
                      >
                        <Link2 size={12} /> {t("invitations.link")}
                      </button>
                    )}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <span className={`text-xs font-semibold ${u.mfa_enabled ? "text-emerald-600" : "text-gray-500"}`}>
                      {u.mfa_enabled ? t("mfa.mfaEnabled") : t("mfa.mfaDisabled")}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-500 whitespace-nowrap">{fmtDate(u.last_login_at)}</td>
                  <td className="px-4 py-3 text-xs text-gray-500 whitespace-nowrap">{fmtDate(u.created_at)}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3 flex-wrap">
                      <button
                        data-testid={`suspend-toggle-${u.id}`}
                        disabled={isSelf}
                        title={isSelf ? t("invitations.cannotModifySelf") : undefined}
                        onClick={() => handleSuspendToggle(u)}
                        className={`flex items-center gap-1 text-xs font-semibold disabled:opacity-40 disabled:cursor-not-allowed ${
                          u.is_suspended ? "text-emerald-600 hover:text-emerald-700" : "text-red-500 hover:text-red-700"
                        }`}
                      >
                        {u.is_suspended ? <UserCheck size={13} /> : <UserX size={13} />}
                        {u.is_suspended ? t("invitations.reactivate") : t("invitations.suspend")}
                      </button>
                      <button
                        data-testid={`revoke-sessions-${u.id}`}
                        disabled={isSelf}
                        title={isSelf ? t("invitations.cannotModifySelf") : undefined}
                        onClick={() => setRevokingSessionsUserId(u.id)}
                        className="flex items-center gap-1 text-xs font-semibold text-gray-500 hover:text-red-600 disabled:opacity-40 disabled:cursor-not-allowed"
                      >
                        <LogOut size={13} /> {t("invitations.revokeSessions")}
                      </button>
                      {canResetMfa && u.mfa_enabled && resetTargetId !== u.id && (
                        <button
                          data-testid={`reset-mfa-button-${u.id}`}
                          disabled={isSelf}
                          title={isSelf ? t("invitations.cannotModifySelf") : undefined}
                          onClick={() => { setResetTargetId(u.id); setResetReason(""); setResetError(""); }}
                          className="flex items-center gap-1 text-xs font-semibold text-red-500 hover:text-red-700 disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                          <ShieldOff size={13} /> {t("mfa.resetMfa")}
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
            {canResetMfa && resetTargetId && (
              <tr>
                <td colSpan={9} className="px-4 py-4 bg-red-50 border-t border-red-100">
                  <form onSubmit={handleResetMfa} className="flex flex-col gap-2" data-testid="reset-mfa-form">
                    <p className="text-xs font-semibold text-red-700">{t("mfa.resetMfaConfirmTitle")}</p>
                    <input
                      data-testid="reset-mfa-reason-input"
                      required
                      placeholder={t("mfa.resetReasonPlaceholder")}
                      value={resetReason}
                      onChange={(e) => setResetReason(e.target.value)}
                      className="w-full px-3 py-2 rounded-lg border border-red-200 text-sm"
                    />
                    {resetError && <p className="text-xs text-red-600" data-testid="reset-mfa-error">{resetError}</p>}
                    <div className="flex gap-2">
                      <button data-testid="submit-reset-mfa-button" type="submit" className="px-4 py-1.5 rounded-full bg-red-600 text-white text-xs font-semibold hover:bg-red-700 active:scale-95 transition-all">
                        {t("mfa.resetMfaSubmit")}
                      </button>
                      <button type="button" onClick={() => setResetTargetId(null)} className="px-4 py-1.5 rounded-full border border-gray-300 text-xs font-semibold text-gray-600 hover:bg-gray-100">
                        {t("common.cancel")}
                      </button>
                    </div>
                  </form>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      {!canManageUsers && (
        <p className="text-xs text-gray-500 mt-4 flex items-center gap-1.5"><ShieldCheck size={13} /> {t("invitations.readOnlyView")}</p>
      )}

      <Dialog open={!!accessDialogUser} onOpenChange={(open) => !open && setAccessDialogUser(null)}>
        <DialogContent data-testid="change-access-dialog">
          <DialogHeader>
            <DialogTitle>{t("invitations.changeAccessDialogTitle", { name: accessDialogUser?.name })}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <p className="text-xs text-slate-500">{t("invitations.changeAccessDialogHelp", { name: accessDialogUser?.name })}</p>
            <Select value={accessProfileChoice} onValueChange={setAccessProfileChoice}>
              <SelectTrigger data-testid="access-profile-select"><SelectValue placeholder={t("invitations.selectAccessProfilePlaceholder")} /></SelectTrigger>
              <SelectContent>
                {rolesForDomain(accessDialogUser?.account_domain).map((r) => (
                  <SelectItem key={r} value={r} data-testid={`access-profile-option-${r}`}>{profileLabelForRole(r, t)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {accessProfileChoice && (
              <p className="text-xs text-slate-500 bg-slate-50 rounded-lg p-3" data-testid="access-profile-description">
                {profileDescriptionForRole(accessProfileChoice, t)}
              </p>
            )}
          </div>
          <DialogFooter>
            <button onClick={() => setAccessDialogUser(null)} className="px-4 py-2 rounded-full border border-slate-300 text-sm font-semibold text-slate-600 hover:bg-slate-50">{t("common.cancel")}</button>
            <button data-testid="save-access-change-button" onClick={handleAccessChange} className="px-4 py-2 rounded-full bg-slate-900 text-white text-sm font-semibold hover:bg-slate-700 active:scale-95 transition-all">{t("invitations.saveAccessChange")}</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={!!revokingInvitationId} onOpenChange={(v) => !v && setRevokingInvitationId(null)}
        title={t("invitations.revokeConfirm")} danger confirmLabel={t("invitations.revoke")}
        onConfirm={() => handleRevoke(revokingInvitationId)} testId="revoke-invitation-dialog"
      />
      <ConfirmDialog
        open={!!suspendingUserId} onOpenChange={(v) => !v && setSuspendingUserId(null)}
        title={`${t("invitations.suspend")}?`} danger confirmLabel={t("invitations.suspend")}
        onConfirm={confirmSuspend} testId="suspend-user-dialog"
      />
      <ConfirmDialog
        open={!!revokingSessionsUserId} onOpenChange={(v) => !v && setRevokingSessionsUserId(null)}
        title={t("invitations.revokeSessionsConfirm")} danger confirmLabel={t("invitations.revokeSessions")}
        onConfirm={confirmRevokeSessions} testId="revoke-sessions-dialog"
      />
      <ConfirmDialog
        open={!!togglingAuthorityUserId} onOpenChange={(v) => !v && setTogglingAuthorityUserId(null)}
        title={
          (users.find((u) => u.id === togglingAuthorityUserId)?.extra_permissions || []).includes("management.user.create")
            ? t("invitations.revokeManagementAuthorityConfirm")
            : t("invitations.grantManagementAuthorityConfirm")
        }
        confirmLabel={t("common.confirm")}
        onConfirm={confirmToggleManagementAuthority} testId="toggle-management-authority-dialog"
      />
      <ConfirmDialog
        open={!!unlinkingUserId} onOpenChange={(v) => !v && setUnlinkingUserId(null)}
        title={t("invitations.unlinkConfirm")} danger confirmLabel={t("invitations.unlink")}
        onConfirm={confirmUnlink} testId="unlink-employee-dialog"
      />
    </div>
  );
}
